import moment from 'moment';
import { ModelBase } from '../abstract/model-base.model';
import { User } from '../user/user.model';

/**
 * État d'une action de l'agent. Écriture directe : `applied` tant que personne n'a tranché ;
 * ensuite conservée, adoptée (objet revu ou utilisé par un humain) ou annulée (bouton
 * Annuler, « Annuler cet échange », ou objet supprimé à la main). Proposition : `proposed`
 * jusqu'à ce qu'un humain l'applique (`applied`, avec `reviewedBy`) ou la refuse
 * (`rejected`). Une action n'est tranchée qu'une fois. L'état `expired` n'est jamais servi.
 */
export type AgentActionProcessingStatus = 'applied' | 'kept' | 'adopted' | 'undone' | 'proposed' | 'rejected';

/** Ce qu'une action écrit, champ par champ, en « libellé : valeur » déjà lisibles. */
export interface AgentActionDetail {
    label: string;
    value: string;
}

/**
 * Objet visé par une action : `model` au format `app.modèle`, libellé du moment de l'action,
 * sans le nom du dossier. `id` nul quand l'objet n'existe pas ou plus : création proposée
 * pas encore appliquée, suppression appliquée, création annulée, objet supprimé.
 */
export interface AgentActionTarget {
    model: string;
    id: number | null;
    label: string;
}

/**
 * Une écriture de l'agent IA (#1217), telle que le chat l'affiche en carte : même forme
 * dans l'event SSE `action`, dans les `actions` d'un tour au rejeu d'une conversation et
 * dans la liste `/api/agent/actions/`.
 *
 * Écriture directe : Annuler / Conserver agissent sur le marqueur (`markerId`), pas sur
 * l'action — `markerId` nul veut dire que l'objet a été revu, conservé ou annulé depuis.
 * Proposition : pas de marqueur, Appliquer / Refuser agissent sur l'action.
 */
export class AgentAction extends ModelBase {

    static override CLASS_NAME: string = 'AgentAction';

    private _canApply!: boolean;
    private _canKeep!: boolean;
    private _canReject!: boolean;
    private _canUndo!: boolean;
    private _details!: AgentActionDetail[];
    private _expiresAt!: moment.Moment | null;
    private _markerId!: number | null;
    private _processingStatus!: AgentActionProcessingStatus;
    private _processingStatusLabel!: string;
    private _reviewedAt!: moment.Moment | null;
    private _reviewedBy!: User | null;
    private _sessionId!: string | null;
    private _summary!: string;
    private _target!: AgentActionTarget | null;
    private _toolLabel!: string;
    private _toolName!: string;
    private _user!: User | null;
    // creationDate est hérité de ModelBase

    /** Proposition : le serveur accepterait que l'utilisateur courant l'applique. */
    public get canApply(): boolean {
        return this._canApply;
    }

    public set canApply(value: any) {
        this._setValue('_canApply', value, 'boolean');
    }

    public get canKeep(): boolean {
        return this._canKeep;
    }

    public set canKeep(value: any) {
        this._setValue('_canKeep', value, 'boolean');
    }

    /** Proposition : l'utilisateur courant peut la refuser (son auteur, ou qui pourrait l'appliquer). */
    public get canReject(): boolean {
        return this._canReject;
    }

    public set canReject(value: any) {
        this._setValue('_canReject', value, 'boolean');
    }

    public get canUndo(): boolean {
        return this._canUndo;
    }

    public set canUndo(value: any) {
        this._setValue('_canUndo', value, 'boolean');
    }

    /** Ce que l'action écrit, champ par champ ; vide pour une suppression. */
    public get details(): AgentActionDetail[] {
        return this._details ?? [];
    }

    public set details(value: any) {
        this._setValue('_details', value, 'any');
    }

    /** Proposition en attente : date d'expiration (création + 7 jours) ; `null` sinon. */
    public get expiresAt(): moment.Moment | null {
        return this._expiresAt;
    }

    public set expiresAt(value: any) {
        this._setValue('_expiresAt', value, 'datetime');
    }

    /** Marqueur qui couvre encore l'action ; `null` quand l'objet a été revu, conservé ou annulé — et pour une proposition. */
    public get markerId(): number | null {
        return this._markerId;
    }

    public set markerId(value: any) {
        this._setValue('_markerId', value, 'integer');
    }

    public get processingStatus(): AgentActionProcessingStatus {
        return this._processingStatus;
    }

    public set processingStatus(value: any) {
        // Le back sérialise l'état en objet { code, label } (ChoiceField).
        const code = value && typeof value === 'object' ? value.code : value;
        const label = value && typeof value === 'object' ? value.label : value;
        this._setValue('_processingStatus', code, 'string');
        this._setValue('_processingStatusLabel', label, 'string');
    }

    public get processingStatusLabel(): string {
        return this._processingStatusLabel;
    }

    /** Proposition en attente d'un humain qui l'applique ou la refuse. */
    public get proposed(): boolean {
        return this._processingStatus === 'proposed';
    }

    /** Quand un humain a tranché l'action ; `null` tant que personne ne l'a fait. */
    public get reviewedAt(): moment.Moment | null {
        return this._reviewedAt;
    }

    public set reviewedAt(value: any) {
        this._setValue('_reviewedAt', value, 'datetime');
    }

    /** L'humain qui a tranché l'action ; `null` tant que personne ne l'a fait. */
    public get reviewedBy(): User | null {
        return this._reviewedBy;
    }

    public set reviewedBy(value: any) {
        this._setValue('_reviewedBy', value, 'User');
    }

    /** Conversation de l'action ; `null` pour un appel MCP. */
    public get sessionId(): string | null {
        return this._sessionId;
    }

    public set sessionId(value: any) {
        this._setValue('_sessionId', value, 'string');
    }

    /** Récapitulatif composé par le serveur, à afficher tel quel. */
    public get summary(): string {
        return this._summary;
    }

    public set summary(value: any) {
        this._setValue('_summary', value, 'string');
    }

    public get target(): AgentActionTarget | null {
        return this._target;
    }

    public set target(value: any) {
        this._setValue('_target', value, 'any');
    }

    public get toolLabel(): string {
        return this._toolLabel;
    }

    public set toolLabel(value: any) {
        this._setValue('_toolLabel', value, 'string');
    }

    public get toolName(): string {
        return this._toolName;
    }

    public set toolName(value: any) {
        this._setValue('_toolName', value, 'string');
    }

    public get undone(): boolean {
        return this._processingStatus === 'undone';
    }

    /** Utilisateur au nom duquel l'agent a écrit. */
    public get user(): User | null {
        return this._user;
    }

    public set user(value: any) {
        this._setValue('_user', value, 'User');
    }
}
